import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import type { CrmNotification } from '@elka/types';
import { useAuth } from '@/features/auth/hooks/useAuth';

export function useCrmNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<CrmNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotifications([]);

      setIsLoading(false);
      return;
    }

    const ref = collection(db, ...splitPath(COLLECTIONS.crm.notifications));
    const q = query(
      ref,
      where('recipientUid', 'in', [user.uid, 'all']),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as CrmNotification);
        setNotifications(docs);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error('CRM notifications listener error:', err);
        setError('שגיאה בטעינת הודעות');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const unreadCount = useMemo(() => {
    if (!user) return 0;
    return notifications.filter((n) => !n.readBy?.[user.uid]).length;
  }, [notifications, user]);

  const markAsRead = async (notificationId: string) => {
    if (!user) return;
    const docRef = doc(db, ...splitPath(COLLECTIONS.crm.notifications), notificationId);
    await updateDoc(docRef, {
      [`readBy.${user.uid}`]: serverTimestamp(),
    });
  };

  const markAllAsRead = async () => {
    if (!user) return;
    const unread = notifications.filter((n) => !n.readBy?.[user.uid]);
    // allSettled (not all) so one rejected write doesn't abort marking the rest read —
    // with Promise.all, a single permission/network failure leaves every other
    // notification still showing as unread.
    const results = await Promise.allSettled(
      unread.map((n) => {
        const docRef = doc(db, ...splitPath(COLLECTIONS.crm.notifications), n.id);
        return updateDoc(docRef, {
          [`readBy.${user.uid}`]: serverTimestamp(),
        });
      })
    );
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length > 0) {
      console.error(`markAllAsRead: ${failed.length}/${unread.length} writes failed`, failed);
    }
  };

  return { notifications, isLoading, error, unreadCount, markAsRead, markAllAsRead };
}
